import { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useClerk, useUser } from '@clerk/clerk-react';

import { buildLabel } from '../build-info.js';

/**
 * Consolidated "who am I + my personal stuff" popover (card 050).
 *
 * Replaces two pieces of the old chrome:
 *
 *   1. Clerk's <UserButton> floating at the bottom of the sidebar.
 *   2. A dedicated "You" section in the sidebar nav with a personal
 *      API keys link.
 *
 * Anchored from the HeaderBar — a single avatar+name button on the
 * right of the top bar.  Click opens a small popover with:
 *
 *   - Name + email row (read-only, the identity card)
 *   - My API keys      → /my-keys (card 053 split — own-only view)
 *   - My profile       → /profile (card 056; honest placeholder
 *                        today via main.jsx routing)
 *   - Sign out         → Clerk.signOut() back to '/'
 *
 * The avatar uses Clerk's user image when present; falls back to
 * the user's initials in a brand-coloured circle so the bar never
 * renders a half-broken Clerk widget while the JS hydrates.
 */
export function UserMenu() {
  const { user } = useUser();
  const { signOut } = useClerk();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);

  // Close on outside click + Escape.  Keeping these as window-level
  // listeners (rather than React handlers on a backdrop) so the
  // popover doesn't intercept clicks on the rest of the page —
  // hitting any link still works while the menu is open.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (!rootRef.current) return;
      if (rootRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus?.();
      }
    };
    window.addEventListener('click', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('click', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const fullName = user?.fullName || user?.firstName || '';
  const email = user?.primaryEmailAddress?.emailAddress || '';
  const displayLabel = fullName || email || 'Account';
  const initials = computeInitials(fullName, email);
  const avatarUrl = user?.imageUrl || null;

  async function handleSignOut() {
    setOpen(false);
    try {
      await signOut({ redirectUrl: '/' });
    } catch {
      /* signOut throws when there's no active session — safe to ignore */
    }
  }

  return (
    <div className="user-menu" ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        data-ui-name="user-menu-trigger"
        className={`user-menu__trigger${open ? ' user-menu__trigger--open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={displayLabel ? `Account menu for ${displayLabel}` : 'Account menu'}
      >
        <Avatar url={avatarUrl} initials={initials} />
        <span className="user-menu__name">{displayLabel}</span>
        <ChevronDownIcon />
      </button>

      {open && (
        <div className="user-menu__popover" role="menu" aria-label="Account menu">
          <div className="user-menu__identity">
            <Avatar url={avatarUrl} initials={initials} size={36} />
            <div className="user-menu__identity-text">
              {fullName && <div className="user-menu__identity-name">{fullName}</div>}
              {email && <div className="user-menu__identity-email">{email}</div>}
              {!fullName && !email && (
                <div className="user-menu__identity-email">Signed in</div>
              )}
            </div>
          </div>

          <div className="user-menu__divider" role="separator" />

          <Link
            to="/my-keys"
            className="user-menu__item"
            role="menuitem"
            onClick={() => setOpen(false)}
          >
            <KeyIcon />
            <span>My API keys</span>
          </Link>
          <Link
            to="/profile"
            data-ui-name="user-menu-profile"
            className="user-menu__item"
            role="menuitem"
            onClick={() => setOpen(false)}
          >
            <UserIcon />
            <span>My profile</span>
          </Link>

          <div className="user-menu__divider" role="separator" />

          <button
            type="button"
            className="user-menu__item user-menu__item--danger"
            role="menuitem"
            onClick={handleSignOut}
          >
            <SignOutIcon />
            <span>Sign out</span>
          </button>

          <BuildFooter />
        </div>
      )}

      <style>{USER_MENU_CSS}</style>
    </div>
  );
}

/**
 * Card 093 — quiet version label at the bottom of the user menu.
 * Tag only.  No commit SHA, no clickable link.  Internal repo and
 * CI identifiers stay out of the customer's bundle.
 *
 * Hover reveals a copy button so the operator can paste the version
 * straight into a bug report.  The whole row is also clickable —
 * keyboard users get the same behaviour via Enter / Space on the
 * focused button.  Two-second "Copied" tick gives confirmation.
 */
function BuildFooter() {
  const label = buildLabel();
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => () => {
    if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
  }, []);

  const onCopy = useCallback(async () => {
    if (!label) return;
    try {
      await navigator.clipboard.writeText(label);
      setCopied(true);
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
      timeoutRef.current = window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API can throw under permissions / insecure-context.
      // Soft-fail: leave `copied` false, no user-visible error.
    }
  }, [label]);

  if (!label) return null;

  return (
    <button
      type="button"
      className="user-menu__build"
      onClick={onCopy}
      aria-label={copied ? `Copied version ${label}` : `Copy version ${label}`}
      title={copied ? 'Copied' : 'Click to copy'}
    >
      <span className="user-menu__build-value">{label}</span>
      <span className="user-menu__build-icon" aria-hidden="true">
        {copied ? <CheckSmallIcon /> : <CopySmallIcon />}
      </span>
    </button>
  );
}

function CopySmallIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function CheckSmallIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="11"
      height="11"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function Avatar({ url, initials, size = 24 }) {
  if (url) {
    return (
      <img
        src={url}
        alt=""
        className="user-menu__avatar"
        style={{ width: size, height: size }}
        referrerPolicy="no-referrer"
      />
    );
  }
  return (
    <span
      className="user-menu__avatar user-menu__avatar--initials"
      style={{ width: size, height: size, fontSize: Math.max(10, Math.round(size * 0.42)) }}
      aria-hidden="true"
    >
      {initials || '?'}
    </span>
  );
}

function computeInitials(fullName, email) {
  const source = (fullName || email || '').trim();
  if (!source) return '';
  if (fullName) {
    const parts = fullName.trim().split(/\s+/).slice(0, 2);
    return parts.map((p) => p[0]?.toUpperCase() || '').join('') || '';
  }
  // Email fallback — first two alpha chars before the @.
  const local = email.split('@')[0] || '';
  return local.replace(/[^a-zA-Z]/g, '').slice(0, 2).toUpperCase();
}

// ───────── icons (Lucide-style, inline)

function ChevronDownIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function KeyIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="7.5" cy="15.5" r="5.5" />
      <path d="m21 2-9.6 9.6" />
      <path d="m15.5 7.5 3 3L22 7l-3-3" />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="8" r="5" />
      <path d="M20 21a8 8 0 1 0-16 0" />
    </svg>
  );
}

function SignOutIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

const USER_MENU_CSS = `
  .user-menu {
    position: relative;
    display: inline-block;
  }

  .user-menu__trigger {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 5px 10px 5px 5px;
    background: transparent;
    border: 1px solid transparent;
    border-radius: var(--r-pill, 999px);
    cursor: pointer;
    font: inherit;
    font-size: 13px;
    color: var(--text-1, #0f172a);
    transition: background 120ms ease, border-color 120ms ease;
    max-width: 220px;
  }

  .user-menu__trigger:hover,
  .user-menu__trigger--open {
    background: var(--surface-1, #f8fafc);
    border-color: var(--border-1, #e2e8f0);
  }

  .user-menu__trigger:focus-visible {
    outline: 2px solid var(--brand-500, #6366f1);
    outline-offset: 1px;
  }

  .user-menu__name {
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 140px;
  }

  .user-menu__avatar {
    border-radius: 50%;
    object-fit: cover;
    flex: 0 0 auto;
  }

  .user-menu__avatar--initials {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: var(--brand-100, #e0e7ff);
    color: var(--brand-700, #4338ca);
    font-weight: 700;
    letter-spacing: 0.02em;
  }

  .user-menu__popover {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    min-width: 240px;
    background: var(--surface-0, #ffffff);
    border: 1px solid var(--border-1, #e2e8f0);
    border-radius: var(--r-md, 10px);
    box-shadow: var(--shadow-2, 0 4px 14px -4px rgba(15, 23, 42, 0.1));
    padding: 6px;
    z-index: 60;
  }

  .user-menu__identity {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 10px 8px;
  }

  .user-menu__identity-text {
    flex: 1;
    min-width: 0;
  }

  .user-menu__identity-name {
    font-size: 13.5px;
    font-weight: 600;
    color: var(--text-1, #0f172a);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .user-menu__identity-email {
    font-size: 12px;
    color: var(--text-3, #64748b);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .user-menu__divider {
    height: 1px;
    background: var(--border-1, #e2e8f0);
    margin: 4px 2px;
  }

  .user-menu__item {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 8px 10px;
    background: transparent;
    border: 0;
    border-radius: var(--r-sm, 6px);
    color: var(--text-1, #0f172a);
    font: inherit;
    font-size: 13px;
    text-align: left;
    text-decoration: none;
    cursor: pointer;
    transition: background 100ms ease, color 100ms ease;
  }

  .user-menu__item:hover {
    background: var(--surface-1, #f1f5f9);
  }

  .user-menu__item--danger {
    color: var(--danger-700, #b91c1c);
  }

  .user-menu__build {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    width: 100%;
    padding: 6px 12px 4px;
    margin-top: 2px;
    background: transparent;
    border: 0;
    color: var(--text-3, #64748b);
    font: inherit;
    font-size: 11px;
    font-variant-numeric: tabular-nums;
    cursor: pointer;
    border-radius: var(--r-sm, 6px);
    transition: background 100ms ease, color 100ms ease;
  }
  .user-menu__build:hover,
  .user-menu__build:focus-visible {
    background: var(--surface-1, #f1f5f9);
    color: var(--text-2, #475569);
  }
  .user-menu__build:focus-visible {
    outline: 2px solid var(--brand-500, #6366f1);
    outline-offset: -2px;
  }
  .user-menu__build-value {
    color: var(--text-3, #94a3b8);
  }
  .user-menu__build:hover .user-menu__build-value {
    color: var(--text-2, #475569);
  }
  .user-menu__build-icon {
    display: inline-flex;
    align-items: center;
    opacity: 0;
    color: var(--text-3, #94a3b8);
    transition: opacity 120ms ease;
  }
  .user-menu__build:hover .user-menu__build-icon,
  .user-menu__build:focus-visible .user-menu__build-icon {
    opacity: 1;
  }

  .user-menu__item--danger:hover {
    background: var(--danger-50, #fee2e2);
  }
`;
